import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { _ as _object_without_properties } from "@swc/helpers/_/_object_without_properties";
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useLayoutEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { KeyboardDatePicker as MuiKeyboardDatePicker } from '@material-ui/pickers';
import cn from 'classnames';
import { format as formatDate, isSameDay } from 'date-fns';
import typography from '../../foundations/typography.module.scss';
import { ColorCalendar, ColorCalendarError } from '../../icons';
import { useBiAction } from '../BiTracking';
import { useDevice } from '../Device';
import { Spinner } from '../Spinner';
import { Tooltip } from '../Tooltip';
import styles from './InlineSelectDatePicker.module.scss';
import { inlineSelectPrimaryColor, inlineSelectStandardColor, useInlineSelectDatePickerStyles } from './InlineSelectDatePicker.styles';
import { defaultDayHighlightSkin } from './InlineSelectDatePicker.utils';
export function InlineSelectDatePicker(props) {
    const { loading, disabled, disableErrors, size = 'medium', autoOk = true, variant = 'inline', onChange, highlightDay, helperText, skin = 'primary', format: formatProp, defaultOpen = false, testId, minWidth: minWidthProp, clearable, clearLabel, PopoverProps, bi, noBi: _noBi } = props, rest = _object_without_properties(props, [
        "loading",
        "disabled",
        "disableErrors",
        "size",
        "autoOk",
        "variant",
        "onChange",
        "highlightDay",
        "helperText",
        "skin",
        "format",
        "defaultOpen",
        "testId",
        "minWidth",
        "clearable",
        "clearLabel",
        "PopoverProps",
        "bi",
        "noBi"
    ]);
    const isDisabled = disabled || loading;
    const inlineSelectColor = skin === 'primary' ? inlineSelectPrimaryColor : inlineSelectStandardColor;
    const { isMobileDevice } = useDevice();
    const overrides = useInlineSelectDatePickerStyles({
        isDisabled,
        inlineSelectColor,
        isMobileDevice
    });
    const disableErrorsProps = disableErrors ? {
        error: false,
        helperText: null
    } : {};
    const [open, setOpen] = useState(defaultOpen);
    const inputVariant = 'standard';
    const format = formatProp || 'MMM dd, yyyy';
    const logBiAction = useBiAction();
    const handleChange = useCallback((date, value)=>{
        const isValid = date instanceof Date && !isNaN(date.getTime());
        if (isValid && (bi === null || bi === void 0 ? void 0 : bi.name)) {
            logBiAction({
                entityType: 'date_picker',
                entityName: bi.name,
                actionType: 'select',
                data: _object_spread_props(_object_spread({}, bi.data), {
                    test_id: testId !== null && testId !== void 0 ? testId : '',
                    selected_value: date.toISOString().split('T')[0]
                })
            });
        }
        onChange(isValid ? new Date(date.setHours(0, 0, 0, 0)) : null, value);
    }, [
        bi,
        logBiAction,
        onChange,
        testId
    ]);
    const handleFocus = ()=>setOpen(true);
    const handleClose = ()=>setOpen(false);
    const [popoverPaper, setPopoverPaper] = useState(null);
    const handleClearDate = useCallback(()=>{
        setOpen(false);
        onChange(null);
    }, [
        onChange
    ]);
    const emptyLabel = rest.emptyLabel;
    const displayValue = props.value ? formatDate(props.value, format) : emptyLabel !== null && emptyLabel !== void 0 ? emptyLabel : '';
    const iconPadding = 28;
    const [inputWidth, setInputWidth] = useState(minWidthProp);
    useLayoutEffect(()=>{
        if (!displayValue) {
            setInputWidth(minWidthProp);
            return;
        }
        const span = document.createElement('span');
        span.style.fontFamily = typography.fontFamilyNormal;
        span.style.fontSize = typography.fontSizeP1;
        span.style.visibility = 'hidden';
        span.style.position = 'absolute';
        span.style.whiteSpace = 'pre';
        span.innerText = displayValue;
        document.body.appendChild(span);
        const measured = span.getBoundingClientRect().width + iconPadding;
        setInputWidth(minWidthProp ? Math.max(measured, minWidthProp) : measured);
        document.body.removeChild(span);
    }, [
        displayValue,
        minWidthProp,
        isMobileDevice
    ]);
    const textField = /*#__PURE__*/ _jsx(MuiKeyboardDatePicker, _object_spread_props(_object_spread({}, rest, disableErrorsProps), {
        "data-testid": testId,
        disabled: isDisabled,
        className: cn(rest.className, overrides.root),
        keyboardIcon: loading ? /*#__PURE__*/ _jsx(Spinner, {
            size: 16,
            testId: testId && "".concat(testId, "-loading")
        }) : /*#__PURE__*/ _jsx("span", {
            style: {
                display: 'inline-flex',
                alignItems: 'center'
            },
            children: props.error ? /*#__PURE__*/ _jsx(ColorCalendarError, {
                style: isDisabled ? {
                    opacity: 0.4
                } : undefined
            }) : /*#__PURE__*/ _jsx(ColorCalendar, {
                style: isDisabled ? {
                    opacity: 0.4
                } : undefined
            })
        }),
        autoOk: autoOk,
        format: format,
        inputVariant: inputVariant,
        variant: variant,
        focused: open,
        size: size,
        open: open,
        helperText: undefined,
        onClick: !isDisabled ? handleFocus : undefined,
        onClose: handleClose,
        KeyboardButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-btn")
        }, props.KeyboardButtonProps),
        leftArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-prev-month")
        }, props.leftArrowButtonProps),
        rightArrowButtonProps: _object_spread({
            'data-testid': testId && "".concat(testId, "-next-month")
        }, props.rightArrowButtonProps),
        InputLabelProps: {
            classes: {
                root: overrides.label
            }
        },
        InputProps: _object_spread({
            disabled: isDisabled,
            readOnly: true,
            style: {
                width: inputWidth
            },
            classes: {
                root: overrides.input,
                adornedEnd: overrides.adornedEnd,
                input: cn(overrides.inputInput)
            }
        }, props.InputProps),
        inputProps: _object_spread_props(_object_spread({
            value: displayValue
        }, props.inputProps), {
            'data-testid': testId && "".concat(testId, "-input")
        }),
        renderDay: (day, selectedDate, dayInCurrentMonth, dayComponent)=>{
            const highlighted = day && dayInCurrentMonth && highlightDay ? highlightDay(day) : false;
            const dayTestId = day && testId ? "".concat(testId, "-day-").concat(day.getMonth() + 1, "-").concat(day.getDate()) : undefined;
            return /*#__PURE__*/ _jsxs("div", {
                className: styles.dayWrapper,
                "data-testid": dayTestId,
                children: [
                    !!highlighted && !(day && selectedDate && isSameDay(day, selectedDate)) && /*#__PURE__*/ _jsx("div", {
                        className: cn(styles.dayHighlight, styles[typeof highlighted === 'string' ? highlighted : defaultDayHighlightSkin]),
                        "data-testid": dayTestId && "".concat(dayTestId, "-highlighted")
                    }),
                    dayComponent
                ]
            });
        },
        PopoverProps: _object_spread_props(_object_spread({}, isMobileDevice && {
            anchorReference: 'anchorPosition',
            anchorPosition: {
                top: window.innerHeight / 2,
                left: window.innerWidth / 2
            },
            anchorOrigin: {
                vertical: 'center',
                horizontal: 'center'
            },
            transformOrigin: {
                vertical: 'center',
                horizontal: 'center'
            },
            marginThreshold: 0
        }, PopoverProps), {
            onEnter: (node, isAppearing)=>{
                var _PopoverProps_onEnter;
                setPopoverPaper(node);
                PopoverProps === null || PopoverProps === void 0 ? void 0 : (_PopoverProps_onEnter = PopoverProps.onEnter) === null || _PopoverProps_onEnter === void 0 ? void 0 : _PopoverProps_onEnter.call(PopoverProps, node, isAppearing);
            },
            onExited: (node)=>{
                var _PopoverProps_onExited;
                setPopoverPaper(null);
                PopoverProps === null || PopoverProps === void 0 ? void 0 : (_PopoverProps_onExited = PopoverProps.onExited) === null || _PopoverProps_onExited === void 0 ? void 0 : _PopoverProps_onExited.call(PopoverProps, node);
            }
        }),
        onChange: handleChange
    }));
    const shouldShowClear = !!clearable && open && props.value != null && !isDisabled;
    const clearPortal = shouldShowClear && popoverPaper ? /*#__PURE__*/ ReactDOM.createPortal(/*#__PURE__*/ _jsx("div", {
        className: styles.clearButtonRow,
        children: /*#__PURE__*/ _jsx("button", {
            type: "button",
            className: styles.clearButton,
            "data-testid": testId && "".concat(testId, "-clear"),
            onMouseDown: (event)=>event.preventDefault(),
            onClick: handleClearDate,
            children: clearLabel !== null && clearLabel !== void 0 ? clearLabel : 'Clear date'
        })
    }), popoverPaper) : null;
    return helperText ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Tooltip, {
                placement: "top",
                title: open ? '' : helperText,
                testId: testId && "".concat(testId, "-tooltip"),
                children: /*#__PURE__*/ _jsx("span", {
                    style: {
                        display: 'inline-block'
                    },
                    children: textField
                })
            }),
            clearPortal
        ]
    }) : /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            textField,
            clearPortal
        ]
    });
}
